import PendingSession from "../components/PendingSession";
import dates from "componentsv2/tools/dates";
import proxy from "api/proxy";
import { layoutParty } from "src/apps/Partyv2/Party.jsx";
import { layout as layoutPatient } from "./ModelPatient";
import { searchSaleOpen } from "../services/sale";
import { useStore, insertRecord } from "storev2/formStore";
import { useContext, isMaintenance } from "../store/useContext";
import { alertMsg } from "../components/Schedule";
import { SCHEDULING_MODE, TYPE_ORDER, INGRESS_SERVICE_HEALTH, TIMEZONE_OFFSET_HOURS } from "./constants";

let _timeSession = null;

function normalizeTimeSession(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const [h = 0, m = 0, s = 0] = ts.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  }
  return null;
}

function optimisticStateUpdate(active, newState) {
  active.state = newState;
  useStore.setState((_s) => ({ ..._s }));
}

// Combina fecha y hora
function buildIsoLocal(appointmentDate, timeValue) {
  const pad = (n) => String(n).padStart(2, "0");
  let dateStr;
  if (typeof appointmentDate === "string") {
    dateStr = appointmentDate.split("T")[0];
  } else {
    const d = appointmentDate;
    dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  let h, m, s;
  if (timeValue instanceof Date) {
    h = timeValue.getHours();
    m = timeValue.getMinutes();
    s = timeValue.getSeconds();
  } else if (typeof timeValue === "string" && timeValue.includes("T")) {
    [h, m, s] = timeValue
      .split("T")[1]
      .replace(/Z$/, "")
      .split(":")
      .map(Number);
  } else if (typeof timeValue === "string") {
    [h, m, s] = timeValue.split(":").map(Number);
  } else {
    return null;
  }

  const totalMinutes = (h || 0) * 60 + (m || 0) + TIMEZONE_OFFSET_HOURS * 60;
  const finalH = Math.floor(totalMinutes / 60) % 24;
  const finalM = totalMinutes % 60;

  return `${dateStr}T${pad(finalH)}:${pad(finalM)}:${pad(s || 0)}`;
}

function setEndTime(active, store, timeSession) {
  if (!active.start_date || !active.appointment_date) return;

  const start_iso = buildIsoLocal(active.appointment_date, active.start_date);
  if (!start_iso) return;

  if (store.store) store.store.start_date = start_iso;
  const startDateLocal = dates.getTrytonDateTimeLocal2Js(start_iso);
  active.start_date = startDateLocal;
  if (store.active) store.active.start_date = startDateLocal;

  const seconds = normalizeTimeSession(timeSession);
  if (!seconds) return;

  const [datePart, timePart] = start_iso.split("T");
  const [h, m, s] = timePart.split(":").map(Number);
  const totalSeconds = h * 3600 + m * 60 + s + seconds;
  const endH = Math.floor(totalSeconds / 3600) % 24;
  const endM = Math.floor((totalSeconds % 3600) / 60);
  const endS = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const end_iso = `${datePart}T${pad(endH)}:${pad(endM)}:${pad(endS)}`;

  if (store.store) store.store.end_date = end_iso;
  const endDateLocal = dates.getTrytonDateTimeLocal2Js(end_iso);
  active.end_date = endDateLocal;
  if (store.active) store.active.end_date = endDateLocal;

  const { updateContext } = useContext.getState();
  const recordId = store.active?.id ?? store.store?.id;
  if (!recordId) return;

  updateContext({ currentEvent: null });
  setTimeout(() => {
    updateContext({ currentEvent: { storeId: store.storeId, id: recordId } });
  }, 0);
}

function getActive(store) {
  return store?.active ?? store?.store ?? {};
}

async function onChangeProduct(_, store) {
  const active = getActive(store);
  if (!active.product?.id) return;
  const { data } = await proxy.search(
    "product.template",
    [["id", "=", active.product.id]],
    ["time_session"],
  );
  _timeSession = normalizeTimeSession(data?.[0]?.time_session);
  if (active.start_date && active.appointment_date) {
    setEndTime(active, store, _timeSession);
  }
}

async function onChangeStartDate(_, store) {
  const active = getActive(store);
  if (!active.start_date || !active.appointment_date) return;
  if (_timeSession == null && active.product?.id) {
    const { data } = await proxy.search(
      "product.template",
      [["id", "=", active.product.id]],
      ["time_session"],
    );
    _timeSession = normalizeTimeSession(data?.[0]?.time_session);
  }
  setEndTime(active, store, _timeSession);
}

function onChangeEndDate(_, store) {
  const active = getActive(store);
  if (!active.end_date || !active.appointment_date) return;
  const end_iso = buildIsoLocal(active.appointment_date, active.end_date);
  if (!end_iso) return;
  if (store.store) store.store.end_date = end_iso;

  // Widget recibe Date local
  const endDateLocal = dates.getTrytonDateTimeLocal2Js(end_iso);
  active.end_date = endDateLocal;
  if (store.active) store.active.end_date = endDateLocal;
}

function onChangeDate(_, store) {
  const active = getActive(store);
  if (!active.appointment_date) return;
  if (active.start_date) {
    const start_iso = buildIsoLocal(active.appointment_date, active.start_date);
    if (start_iso) {
      active.start_date = start_iso;
      if (store.store) store.store.start_date = start_iso;
      if (store.active) store.active.start_date = start_iso;
    }
  }
  if (active.end_date) {
    const end_iso = buildIsoLocal(active.appointment_date, active.end_date);
    if (end_iso) {
      active.end_date = end_iso;
      if (store.store) store.store.end_date = end_iso;
      if (store.active) store.active.end_date = end_iso;
    }
  }
}

async function syncPatientFromCustomer(customerId, store) {
  if (!customerId) return;
  const { data } = await proxy.search(
    "party.party",
    [
      ["id", "=", customerId],
      ["is_patient", "=", true],
    ],
    ["id", "rec_name"],
  );
  let patient = null;

  if (data?.length > 0) {
    patient = data[0];
  } else {
    const { data: partyData } = await proxy.browse(
      "party.party",
      [customerId],
      ["id", "rec_name", "born_country", "country"],
    );
    if (!partyData?.length) return;

    const res = await proxy.save({
      model: "party.party",
      ids: [customerId],
      values: {
        is_patient: true,
        type_user_health: "12", //Particular
        born_country: partyData[0].born_country || partyData[0].country,
      },
    });
    if (res?.error) return;
    const { data: updated } = await proxy.search(
      "party.party",
      [["id", "=", customerId]],
      ["id", "rec_name"],
    );
    if (updated?.length > 0) patient = updated[0];
  }

  if (!patient) return;
  if (store.store) store.store.patient = patient.id;
  if (store.active) store.active.patient = patient;

  const { updateContext } = useContext.getState();
  const recordId = store.active?.id ?? store.store?.id;
  if (!recordId) return;

  updateContext({ currentEvent: null });
  setTimeout(() => {
    updateContext({ currentEvent: { storeId: store.storeId, id: recordId } });
  }, 0);
}

function onChangeTypeOrder(_, store) {
  const active = getActive(store);
  if (active.type_order?.id !== "particular") return;
  const customerId =
    store.active?.customer?.id ??
    store.store?.customer?.id ??
    store.store?.customer;
  syncPatientFromCustomer(customerId, store);
}

async function onChangeCustomer(storeId, store) {
  const active = getActive(store);
  const customerId =
    active?.customer?.id ?? store.store?.customer?.id ?? store.store?.customer;

  if (!customerId || customerId < 0) return;

  const saleOpen = await searchSaleOpen(active);

  const { data: pending_sessions } = await proxy.search(
    "appointment_scheduling",
    [
      ["state", "=", "draft"],
      ["customer", "=", customerId],
    ],
    [
      "id",
      "customer.rec_name",
      "professional.rec_name",
      "product.name",
      "product.time_session",
      "product.list_price",
      "product.sale_uom",
      "product.template.account_category.customer_taxes",
      "product.quantity_mix_required",
      "product.type_mix",
      "consumer.rec_name",
      "sale.rec_name",
      "state",
      "number",
    ],
  );

  active.pending_sessions = pending_sessions.length;
  active.sessions = pending_sessions;

  if (saleOpen) {
    setSale(active, store, saleOpen);
  }

  const typeOrder =
    active.type_order?.id ?? active.type_order ?? store.store?.type_order;

  if (typeOrder === "particular") {
    await syncPatientFromCustomer(customerId, store, storeId);
  }

  const rec = useStore.getState()[storeId];
  if (rec) {
    rec.active.pending_sessions = pending_sessions.length;
    rec.active.sessions = pending_sessions;
    if (saleOpen) {
      rec.active.sale = saleOpen;
      rec.store.sale = saleOpen.id;
    }
    useStore.setState({ [storeId]: { ...rec } });
  }
}

function setSale(active, store, sale) {
  if (!sale) return;
  active.sale = sale;
  if (store.store) store.store.sale = sale.id;
  if (store.active) store.active.sale = sale;
}

function readOnlySale(record) {
  return !record || !["draft", "scheduled", "show_up"].includes(record.state);
}

function readOnlyProfessional(record) {
  return !record || !["draft", "scheduled"].includes(record.state);
}

function readOnlyState(record) {
  return !record || !["draft", "scheduled"].includes(record.state);
}

function onChangeSale(_, store) {
  const active = getActive(store);
  setSale(active, store, active.sale);
}

function searchPatient(value) {
  const base = [["is_patient", "=", true]];
  if (!value) return base;
  return [
    ...base,
    [
      "OR",
      ["name", "ilike", `%${value}%`],
      ["id_number", "ilike", `%${value}%`],
    ],
  ];
}

function searchSale(_, record) {
  let dom = [["id", "=", -1]];
  if (record.customer) {
    dom = [
      ["customer", "=", record.customer?.id],
      ["state", "=", "draft"],
    ];
  }
  return dom;
}

function searchProduct(value) {
  const dom = [["salable", "=", true]];
  if (value) {
    const target_words = value.split(" ");
    target_words.forEach((tw) => {
      if (tw.length <= 1) return;
      dom.push([
        "OR",
        ["name", "ilike", `%${tw}%`],
        ["code", "ilike", `%${tw}%`],
        ["reference", "=", `%${tw}%`],
      ]);
    });
  }

  return dom;
}

function invisibleButton(btn, record) {
  if (!record?.id || record.id < 0) return true;
  const state = record.state;
  const today = dates.startOfToday();

  if (btn === "schedule") {
    return state !== "draft";
  }
  if (btn === "show_up") {
    return !(state === "scheduled" && record.appointment_date <= today);
  }
  if (btn === "attended") {
    return state !== "show_up";
  }
  if (btn === "no_show_up" || btn === "cancel") {
    return !(state === "scheduled");
  }
  if (btn === "add_new") {
    const notInPast = record.appointment_date < today;
    const validState = ["scheduled", "show_up", "attended"].includes(state);
    return !(validState && !notInPast);
  }
  if (btn === "pending_sessions") {
    return !(record?.id < 0 || typeof record?.id === "undefined");
  }
  return true;
}

async function onSchedule(record) {
  const active = record?.active ?? record;

  if (!active?.id || active.id < 0) return;

  try {
    const res = await proxy.save({
      model: "appointment_scheduling",
      ids: [active.id],
      values: { state: "scheduled" },
    });

    if (res?.error) {
      const errorMsg =
        typeof res.error === "string"
          ? res.error
          : (res.error?.message ?? JSON.stringify(res.error));

      alertMsg.value = {
        show: true,
        text: `appointment_scheduling.messge_error: ${errorMsg}`,
        icon: "xmark",
        color: "bg-red-200",
        autoRemove: true,
      };

      return res;
    }

    optimisticStateUpdate(active, "scheduled");

    await insertRecord(active.id, "appointment_scheduling", layout());

    alertMsg.value = {
      show: true,
      text: "appointment_scheduling.schedule_message",
      icon: "check",
      color: "bg-green-200",
      autoRemove: true,
    };

    return res;
  } catch (e) {
    console.error(e);

    alertMsg.value = {
      show: true,
      text: "appointment_scheduling.messge_error",
      icon: "xmark",
      color: "bg-red-200",
      autoRemove: true,
    };
  }
}

async function onAttended(record) {
  if (!record) return;
  const active = record?.active ?? record;

  if (!active.sale && !active.non_billable) {
    await proxy.buttonMethod({
      model: "appointment_scheduling",
      method: "add_services",
      ids: [active.id],
    });
  }

  const res = await proxy.buttonMethod({
    model: "appointment_scheduling",
    method: "attended",
    ids: [active.id],
  });

  if (res?.error) return res;

  optimisticStateUpdate(active, "attended");

  await insertRecord(active.id, "appointment_scheduling", layout());

  alertMsg.value = {
    show: true,
    text: "appointment_scheduling.attended_message",
    icon: "check",
    color: "bg-green-200",
    autoRemove: true,
  };

  return res;
}

async function onShowUp(record) {
  const active = record?.active ?? record;
  if (!active?.id || active.id < 0) return;

  const res = await proxy.buttonMethod({
    model: "appointment_scheduling",
    method: "show_up",
    ids: [active.id],
  });

  if (res?.error) return res;

  await insertRecord(active.id, "appointment_scheduling", layout());
  optimisticStateUpdate(active, "show_up");

  alertMsg.value = {
    show: true,
    text: "appointment_scheduling.show_up_message",
    icon: "check",
    color: "bg-green-200",
    autoRemove: true,
  };

  return res;
}

async function onNoShowUp(record) {
  const active = record?.active ?? record;
  if (!active?.id || active.id < 0) return;

  const res = await proxy.buttonMethod({
    model: "appointment_scheduling",
    method: "no_show_up",
    ids: [active.id],
  });

  if (res?.error) return res;

  optimisticStateUpdate(active, "no_show_up");
  await insertRecord(active.id, "appointment_scheduling", layout());

  alertMsg.value = {
    show: true,
    text: "appointment_scheduling.no_show_up_message",
    icon: "check",
    color: "bg-green-200",
    autoRemove: true,
  };

  return res;
}

async function onCancel(record) {
  const active = record?.active ?? record;
  if (!active?.id || active.id < 0) return;

  const res = await proxy.buttonMethod({
    model: "appointment_scheduling",
    method: "cancel",
    ids: [active.id],
  });

  if (res?.error) return res;

  await insertRecord(active.id, "appointment_scheduling", layout());
  optimisticStateUpdate(active, "cancelled");

  alertMsg.value = {
    show: true,
    text: "Cita cancelada exitosamente",
    icon: "check",
    color: "bg-green-200",
    autoRemove: true,
  };

  return res;
}

function customerRequired(record) {
  const typeOrder = record?.type_order?.id ?? record?.type_order;
  if (isMaintenance(record)) return false;
  return typeOrder === "company";
}

function maintenanceRequired(record) {
  return !isMaintenance(record);
}

async function onChangeAppointmentType(_, store) {
  const active = getActive(store);
  const id = active.appointment_type?.id;
  if (!id) return;

  const { data } = await proxy.search( "appointment_scheduling.appointment_type", [["id", "=", id]], ["maintenance"]);
  active.appointment_type = { ...active.appointment_type, maintenance: data?.[0]?.maintenance ?? false };
}

async function addNewAppointment(record) {
  const { newRecord, updateForm } = useStore.getState();
  const { updateContext } = useContext.getState();

  const current = record?.active ?? record;
  const storeId = await newRecord(layout());
  const currentStore = useStore.getState()[storeId];

  const newId =
    currentStore?.active?.id ?? currentStore?.store?.id ?? -Date.now();

  const newActive = {
    id: newId,
    appointment_date: null,
    state: "draft",
    start_date: current.start_date,
    end_date: current.end_date,
    scheduling_mode: current.scheduling_mode ?? null,
    type_order: current.type_order ?? null,
    customer: current.customer ?? null,
    patient: current.patient ?? null,
    room: current.room ?? null,
    comment: current.comment,
    ingress_service_health: current.ingress_service_health ?? {
      id: "02",
      name: "Derivado de consulta externa",
    },
  };

  const newStore = {
    id: newId,
    appointment_date: null,
    state: "draft",
    start_date: null,
    end_date: null,
    scheduling_mode: current.scheduling_mode,
    type_order: current.type_order?.id,
    customer: current.customer,
    patient: current.patient,
    room: current.room,
    comment: current.comment,
    ingress_service_health: current.ingress_service_health ?? {
      id: "02",
      name: "Derivado de consulta externa",
    },
  };

  updateForm(storeId, newActive, newStore);

  updateContext({ currentEvent: null });

  setTimeout(() => {
    updateContext({ currentEvent: { storeId } });
  }, 0);
}
function layout() {
  return {
    model: "appointment_scheduling",
    orderBy: [
      ["appointment_date", "ASC"],
      ["start_date", "ASC"],
    ],
    form_action: ["add", "edit"],
    table_action: ["open", "add"],
    limit: 50,
    form_toolbar: false,
    fields: {
      type_order: {
        type: "selection",
        options: TYPE_ORDER,
        required: true,
        withChange: onChangeTypeOrder,
      },
      number: { type: "char", readOnly: true },
      customer: {
        type: "many2one",
        model: "party.party",
        layout: layoutParty(),
        withChange: onChangeCustomer,
        filter: true,
        getRequired: customerRequired,
      },
      patient: {
        type: "many2one",
        model: "party.party",
        layout: layoutPatient(),
        recSearch: searchPatient,
        required: maintenanceRequired,
        filter: true,
        withChange: (storeId) => {
          const rec = useStore.getState()[storeId];
          if (rec) {
            useStore.setState({ [storeId]: { ...rec } });
          }
        },
      },
      product: {
        type: "many2one",
        model: "product.template",
        recSearch: searchProduct,
        withChange: onChangeProduct,
        required: maintenanceRequired,
        readOnly: readOnlySale,
      },
      scheduling_mode: {
        type: "selection",
        options: SCHEDULING_MODE,
        required: true,
        default: { id: "room", name: "By Room" },
      },
      professional: {
        type: "many2one",
        model: "appointment_scheduling.professional",
        readOnly: readOnlyProfessional,
      },
      room: {
        type: "many2one",
        model: "appointment_scheduling.room",
        required: true,
        readOnly: readOnlyProfessional,
      },
      appointment_date: {
        type: "date_2",
        required: true,
        withChange: onChangeDate,
        readOnly: readOnlyState,
      },
      start_date: {
        type: "time",
        noCalendar: true,
        required: true,
        withChange: onChangeStartDate,
        readOnly: readOnlyState,
      },
      end_date: {
        type: "time",
        noCalendar: true,
        required: true,
        withChange: onChangeEndDate,
        readOnly: readOnlyState,
      },
      sale: {
        type: "many2one",
        model: "sale.sale",
        recSearch: searchSale,
        withChange: onChangeSale,
        readOnly: true,
      },
      appointment_type: {
        type: "many2one",
        model: "appointment_scheduling.appointment_type",
        withChange: onChangeAppointmentType,
      },
      reference: { type: "char" },
      ingress_service_health: {
        type: "selection",
        options: INGRESS_SERVICE_HEALTH,
        default: { id: "02", name: "Derivado de consulta externa" },
      },
      description: { type: "char", rows: 1, required: true },
      service_order: {
        type: "many2one",
        model: "laboratory.order",
        readOnly: true,
      },
      comment: { type: "char", rows: 1 },
      state: {
        type: "char",
        readOnly: true,
        default: "draft",
      },
      schedule: {
        type: "button",
        method: onSchedule,
        invisible: invisibleButton,
        color: "blue",
      },
      show_up: {
        type: "button",
        method: onShowUp,
        invisible: invisibleButton,
        color: "sky",
      },
      attended: {
        type: "button",
        method: onAttended,
        invisible: invisibleButton,
        color: "green",
      },
      no_show_up: {
        type: "button",
        method: onNoShowUp,
        invisible: invisibleButton,
        color: "amber",
      },
      cancel: {
        type: "button",
        method: onCancel,
        invisible: invisibleButton,
        color: "rose",
      },
      add_new: {
        type: "button",
        method: addNewAppointment,
        invisible: invisibleButton,
        color: "indigo",
      },
      pending_sessions: {
        type: "button_custom_modal",
        Component: PendingSession,
        invisible: invisibleButton,
      },
    },

    form: [
      {
        name: "block",
        type: "grid",
        col: "grid-cols-1 md:grid-cols-2",
        colSpan: "md:col-span-2",
        elements: [
          { name: "type_order" },
          { name: "customer", widget: "search" },
          { name: "patient", widget: "search" },
        ],
      },
      {
        name: "block1",
        type: "grid",
        col: "grid-cols-1 md:grid-cols-3",
        elements: [{ name: "scheduling_mode" }, { name: "room" }],
      },
      {
        name: "block2",
        type: "grid",
        col: "grid-cols-1 md:grid-cols-3",
        colSpan: "md:col-span-4",
        elements: [
          { name: "product" },
          { name: "appointment_date" },
          { name: "start_date" },
          { name: "end_date" },
        ],
      },
      {
        name: "block3",
        type: "grid",
        col: "grid-cols-1 md:grid-cols-2",
        elements: [
          { name: "reference" },
          { name: "ingress_service_health" },
          { name: "appointment_type" },
        ],
      },
      {
        name: "block4",
        type: "grid",
        col: "grid-cols-1",
        elements: [{ name: "service_order" }],
      },
      {
        name: "block5",
        type: "grid",
        col: "grid-cols-1",
        colSpan: "md:col-span-4",
        elements: [{ name: "description" }, { name: "comment" }],
      },
      {
        name: "actions",
        type: "grid",
        col: "grid-cols-2 md:grid-cols-6",
        elements: [
          { name: "schedule" },
          { name: "show_up" },
          { name: "no_show_up" },
          { name: "cancel" },
          { name: "attended" },
          { name: "add_new" },
        ],
      },
    ],
  };
}

export { layout };
